import { WSPosterProps } from "@wingspanhq/fe-component-library";
import {
  BiometricIdentityVerificationRequest,
  BiometricIdentityVerificationRequestStatus,
  Payer,
  PayerTaxInformationShareStatus
} from "@wingspanhq/wingspan-api-sdk";

import { selectorCounterpartyDisplayName } from "../../../../../../shared/selectors/selectorCounterpartyDisplayName";
import { mapStatusesToCardDetails } from "../../../../helpers/mapStatusesToCardDetails";
import { RequirementTableItemData } from "../../../../types";

export const payerIdentityVerificationRenderCardDescription = (
  requirement: RequirementTableItemData,
  identityVerification: BiometricIdentityVerificationRequest | undefined,
  payer: Payer | undefined
) => {
  // v3: tax-information sharing lives on Payer.taxInformationShare (share/revoke
  // verbs), replacing v1 payeeOwnedData.shareTaxDocument.
  const isInfoShared =
    payer?.taxInformationShare?.status ===
    PayerTaxInformationShareStatus.ACTIVE;

  // TODO(v3): this is the one payer name in the app not sourced from the search
  // row's server-resolved `profile.resolvedName`. The `Payer` arrives from
  // `getRequirementPayerFromCache`, which reads the `getPayer` cache keyed by
  // `requirement.payerAccountId` — and a v3 `Payer.id` is not the payer's
  // account id, so that read never hits and this renders empty. Fixing it means
  // moving that helper (and the consent gate it also feeds) onto a payer-row
  // lookup keyed by `payerAccountId`.
  const payerName = payer ? selectorCounterpartyDisplayName(payer) : "";

  const pendingDescription: WSPosterProps = isInfoShared
    ? {
        type: "people",
        title: "Your payer has requested an ID verification to be completed",
        description:
          "Click start to open the ID verification in a pop-up. When you have uploaded the acceptable documents and the verification process is complete, the results will be shown here."
      }
    : {
        type: "people",
        title: "Your payer has requested an ID verification to be completed",
        description: `To proceed with verification process, you need to consent to share your ID verification information with ${payerName}.`
      };

  const failedDescription: WSPosterProps = {
    type: "people",
    title: "Something went wrong with your ID verification",
    description: "Please contact your payer to resolve the issue."
  };

  const descriptions: Record<
    BiometricIdentityVerificationRequestStatus,
    WSPosterProps
  > = {
    [BiometricIdentityVerificationRequestStatus.PENDING]: pendingDescription,
    [BiometricIdentityVerificationRequestStatus.COMPLETE]: {
      type: "done",
      title: "Your ID verification is complete",
      description:
        "Your identity verification and biometrics matching is complete. Your payer will also be notified."
    },
    [BiometricIdentityVerificationRequestStatus.FAILED]: failedDescription
  };

  return mapStatusesToCardDetails<BiometricIdentityVerificationRequestStatus>({
    requirement,
    documentStatus:
      identityVerification?.status ||
      BiometricIdentityVerificationRequestStatus.PENDING,
    documentStatuses: descriptions,
    pendingApprovalStatus: {
      type: "people",
      title: "Your ID verification is waiting approval",
      description:
        "Your identity verification and biometrics matching is waiting approval from your payer."
    },
    approvedStatus: {
      type: "people",
      title: "Your ID verification was approved",
      description:
        "Your identity verification and biometrics matching was approved by the client. There is no further action to take at the moment."
    },
    rejectedStatus: {
      type: "people",
      title: "Your ID verification was rejected",
      description:
        "Your identity verification and biometrics matching was rejected by the client. There is no further action to take at the moment. If you think this is a mistake, please contact your payer to resolve the issue."
    }
  });
};
